<script setup lang="ts">
/**
 * PRIMARY actions: what the thing in front of the user can do — convert this
 * customer file, release this quote, open this section's own entries.
 * Full-width outline buttons, icon then label, one per row. The label doubles
 * as the tooltip, so a truncated one stays readable.
 *
 * Its counterpart is `NxActionToolbar`, the icon-only row of record-
 * INDEPENDENT tools (print, mail, export, history). The kit version of the
 * ERP's `PrimaryActions`, with the same `items` shape (`NxActionElement`).
 */
import { NxButton } from '../Button';
import type { NxActionElement } from './types';

defineProps<{ items: readonly NxActionElement[] }>();
</script>

<template>
  <div class="nx-action-list">
    <NxButton
      v-for="(item, idx) in items"
      :key="item.id ?? idx"
      class="nx-action-list__btn"
      height="xs"
      width="full"
      :variant="item.tone === 'danger' ? 'outline-danger' : 'outline'"
      :title="item.label"
      :disabled="item.disabled"
      :data-test="item.dataTest"
      @click="item.onClick()"
    >
      <component
        :is="item.icon.component"
        v-if="item.icon"
        :filled="item.icon.filled"
        :color="item.icon.color"
        class="nx-action-list__icon nxt-icon-md"
        aria-hidden="true"
      />
      <span class="nx-action-list__label">{{ item.label }}</span>
    </NxButton>
  </div>
</template>

<style scoped>
.nx-action-list {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  width: 100%;
  min-width: 0;
}
.nx-action-list__icon {
  flex: 0 0 auto;
}
.nx-action-list__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: start;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
